/**
 * ScrapeKing Design System v2 - Tabs Component
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, useState, createContext, useContext } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

interface TabsContextType {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const TabsWrapper = styled.div`
  width: 100%;
`;

const TabList = styled.div<{ $variant: 'default' | 'pills' | 'underline' }>`
  display: flex;
  gap: ${props => props.$variant === 'pills' ? 'var(--sk-space-2)' : '0'};
  border-bottom: ${props => props.$variant === 'underline' ? '1px solid var(--sk-surface-border)' : 'none'};
  background: ${props => props.$variant === 'default' ? 'var(--sk-bg-secondary)' : 'transparent'};
  border-radius: ${props => props.$variant === 'default' ? 'var(--sk-radius-default)' : '0'};
  padding: ${props => props.$variant === 'default' ? 'var(--sk-space-1)' : '0'};
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--sk-surface-border);
    border-radius: var(--sk-radius-full);
  }
`;

const TabButton = styled.button<{ $active: boolean; $variant: 'default' | 'pills' | 'underline' }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: var(--sk-space-2) var(--sk-space-4);
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-sm);
  font-weight: 500;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  position: relative;

  ${props => props.$variant === 'default' && css`
    background: ${props.$active ? 'var(--sk-surface-default)' : 'transparent'};
    color: ${props.$active ? 'var(--sk-text-primary)' : 'var(--sk-text-tertiary)'};
    border-radius: var(--sk-radius-sm);
    box-shadow: ${props.$active ? '0 1px 3px rgba(0, 0, 0, 0.2)' : 'none'};

    &:hover {
      color: var(--sk-text-primary);
    }
  `}

  ${props => props.$variant === 'pills' && css`
    background: ${props.$active ? 'var(--sk-cyan-600)' : 'var(--sk-surface-default)'};
    color: ${props.$active ? 'var(--sk-text-primary)' : 'var(--sk-text-secondary)'};
    border-radius: var(--sk-radius-full);
    border: 1px solid ${props.$active ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)'};

    &:hover {
      border-color: ${props.$active ? 'var(--sk-cyan-400)' : 'var(--sk-surface-border-hover)'};
    }

    ${props.$active && css`
      box-shadow: 0 0 15px var(--sk-cyan-glow);
    `}
  `}

  ${props => props.$variant === 'underline' && css`
    background: transparent;
    color: ${props.$active ? 'var(--sk-cyan-500)' : 'var(--sk-text-tertiary)'};
    padding-bottom: var(--sk-space-3);
    margin-bottom: -1px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--sk-cyan-500);
      transform: scaleX(${props.$active ? 1 : 0});
      transition: transform 0.2s ease;
    }

    &:hover {
      color: ${props.$active ? 'var(--sk-cyan-400)' : 'var(--sk-text-secondary)'};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 var(--sk-space-1);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-2xs);
  font-weight: 600;
  background: var(--sk-surface-default);
  border-radius: var(--sk-radius-full);
  color: var(--sk-text-secondary);
`;

const TabPanels = styled.div`
  margin-top: var(--sk-space-4);
`;

const TabPanel = styled.div<{ $active: boolean }>`
  display: ${props => props.$active ? 'block' : 'none'};
  animation: ${props => props.$active ? fadeIn : 'none'} 0.3s ease;
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  children: React.ReactNode;
  variant?: 'default' | 'pills' | 'underline';
}

interface TabProps {
  value: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  badge?: string | number;
  disabled?: boolean;
}

interface TabPanelProps {
  value: string;
  children: React.ReactNode;
}

export const Tabs: FC<TabsProps> & {
  List: typeof TabListComponent;
  Tab: typeof Tab;
  Panels: typeof TabPanelsComponent;
  Panel: typeof Panel;
} = ({
  defaultValue,
  value,
  onChange,
  children,
  variant = 'default',
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const activeTab = value !== undefined ? value : internalValue;

  const setActiveTab = (id: string) => {
    if (onChange) {
      onChange(id);
    } else {
      setInternalValue(id);
    }
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <TabsWrapper data-variant={variant}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, { variant });
          }
          return child;
        })}
      </TabsWrapper>
    </TabsContext.Provider>
  );
};

const TabListComponent: FC<{ children: React.ReactNode; variant?: 'default' | 'pills' | 'underline' }> = ({
  children,
  variant = 'default'
}) => {
  return (
    <TabList role="tablist" $variant={variant}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, { variant });
        }
        return child;
      })}
    </TabList>
  );
};

const Tab: FC<TabProps & { variant?: 'default' | 'pills' | 'underline' }> = ({
  value,
  children,
  icon,
  badge,
  disabled,
  variant = 'default',
}) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error('Tab must be used within Tabs');

  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;

  return (
    <TabButton
      role="tab"
      aria-selected={isActive}
      $active={isActive}
      $variant={variant}
      onClick={() => setActiveTab(value)}
      disabled={disabled}
    >
      {icon}
      {children}
      {badge !== undefined && <TabBadge>{badge}</TabBadge>}
    </TabButton>
  );
};

const TabPanelsComponent: FC<{ children: React.ReactNode }> = ({ children }) => {
  return <TabPanels>{children}</TabPanels>;
};

const Panel: FC<TabPanelProps> = ({ value, children }) => {
  const context = useContext(TabsContext);
  if (!context) throw new Error('TabPanel must be used within Tabs');

  const isActive = context.activeTab === value;

  return (
    <TabPanel role="tabpanel" $active={isActive}>
      {children}
    </TabPanel>
  );
};

Tabs.List = TabListComponent;
Tabs.Tab = Tab;
Tabs.Panels = TabPanelsComponent;
Tabs.Panel = Panel;

export default Tabs;
